import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Tooltip,
  Typography,
  Chip,
  Box,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { PortalRole, UserProfile } from 'src/backend';
import { getUserProfile } from 'src/components/UserStore/userProfileSlice';
import ModalDialog from 'src/components/ModalDialog';
import { MachineSummary } from './MachineGroupMachines';

type Params = {
  machineSummary: MachineSummary | undefined;
};

function MachineStatusSummary({ machineSummary }: Params) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const userProfile = useSelector(getUserProfile) as UserProfile;
  const isAdmin = userProfile?.role === PortalRole[PortalRole.Admin];

  const isInSync =
    machineSummary?.presetSummaryAggregate.excess.count === 0 &&
    machineSummary?.presetSummaryAggregate.missing.count === 0 &&
    machineSummary?.presetSummaryAggregate.outdated.count === 0;

  let text = 'No Data';
  let color: 'success' | 'warning' | 'secondary' = 'secondary';

  if (machineSummary) {
    text = isInSync ? 'In Sync' : 'Out of Sync';
    color = isInSync ? 'success' : 'warning';
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderChip = () => (
    <Chip
      label={<Typography variant="caption">{text}</Typography>}
      sx={{ width: '10rem' }}
      color={color}
    />
  );

  const renderButton = () => (
    <Chip
      label={<Typography variant="caption">{text}</Typography>}
      sx={{
        width: '10rem',
        cursor: 'pointer',
        '&:hover .MuiTypography-root': {
          color: '#fff',
          fontWeight: 500,
        },
      }}
      color={color}
      onClick={handleOpenModal}
    />
  );

  return (
    <>
      <Tooltip
        title={
          <>
            <Typography sx={{ marginBottom: '0.2rem' }}>
              Preset summary
            </Typography>
            <text>
              Missing: {machineSummary?.presetSummaryAggregate.missing.count}
            </text>
            <br />
            <text>
              Excess: {machineSummary?.presetSummaryAggregate.excess.count}
            </text>
            <br />
            <text>
              Outdated: {machineSummary?.presetSummaryAggregate.outdated.count}
            </text>
          </>
        }
      >
        {isAdmin ? renderButton() : renderChip()}
      </Tooltip>

      <ModalDialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title="Preset Details"
        showCancelBtn={false}
        confirmBtnText="Close"
        closeOnConfirm
        maxWidth="md"
        fullWidth
      >
        <Box display="flex" gap={2} sx={{ minHeight: '300px' }}>
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              Missing (
              {machineSummary?.presetSummaryAggregate.missing.count || 0})
            </Typography>
            <List dense>
              {machineSummary?.presetSummaryAggregate.missing.names.map(
                (name) => (
                  <ListItem key={name}>
                    <ListItemText primary={name} />
                  </ListItem>
                ),
              )}
            </List>
          </Box>
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              Excess ({machineSummary?.presetSummaryAggregate.excess.count || 0}
              )
            </Typography>
            <List dense>
              {machineSummary?.presetSummaryAggregate.excess.names.map(
                (name) => (
                  <ListItem key={name}>
                    <ListItemText primary={name} />
                  </ListItem>
                ),
              )}
            </List>
          </Box>
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              Outdated (
              {machineSummary?.presetSummaryAggregate.outdated.count || 0})
            </Typography>
            <List dense>
              {machineSummary?.presetSummaryAggregate.outdated.names.map(
                (name) => (
                  <ListItem key={name}>
                    <ListItemText primary={name} />
                  </ListItem>
                ),
              )}
            </List>
          </Box>
        </Box>
      </ModalDialog>
    </>
  );
}

export default MachineStatusSummary;
